import { makePaginatedSearchWithCount } from "api/makeApiRequestType/makePaginatedSearchWithCount";

import { MentorProfileData, RawApiOutput } from "api/getUserProfile";
import { formatDataForGetRequest } from "api/utils/formatDataForGetRequest";
import { userProfileFromApiToLocal } from "api/utils/userProfileFromApiToLocal";
import { captureMessage } from "services/captureMessage";
import { flagValidationErrors as flagValidationErrorsInProfile } from "api/getUserProfile";

type Params = {
	cursor?: number;
	cursorType?: "after" | "before";
	location?: { locationDistrict: string; locationCountryCode: string };
	canhelpwith?: string[];
	specialistknowledge?: string[];
};

interface MentorSearchResult
	extends Omit<
		MentorProfileData,
		| "paymentProvidersMBWay"
		| "paymentProvidersWisetag"
		| "paymentProvidersCustom"
	> {
	sortindex: number;
}

export const searchForMentors = makePaginatedSearchWithCount<
	Params,
	RawApiOutput<MentorSearchResult>,
	MentorSearchResult
>({
	endPoint: "mentors",
	flagValidationErrorsInRow: flagValidationErrorsInProfile,
	preProcessor: ({
		cursor,
		cursorType,
		location,
		canhelpwith,
		specialistknowledge
	}) => {
		return formatDataForGetRequest({
			cursor: cursor ? cursor + "" : undefined,
			cursorType: cursorType ? cursorType + "" : undefined,
			...(location ? { location: JSON.stringify(location) } : {}),
			...(canhelpwith ? { canhelpwith: JSON.stringify(canhelpwith) } : {}),
			...(specialistknowledge
				? { specialistknowledge: JSON.stringify(specialistknowledge) }
				: {})
		});
	},
	postProcessor: convertRow
});

export function convertRow(profile: RawApiOutput<MentorSearchResult>) {
	const defaultSortIndex = 1;
	if (profile.sortindex === undefined) {
		captureMessage(
			`No sortindex in mentor profile - using '${defaultSortIndex}' instead.`,
			{ evtType: "mentorProfileNoSortIndex" }
		);
	}
	let sortindex: number =
		profile.sortindex === undefined ? defaultSortIndex : profile.sortindex;

	const convertedProfile = userProfileFromApiToLocal(profile);

	const {
		terms,
		hasLinkedZoomAccount,
		canAcceptPayments,
		hasSkypeId,
		mentorProfileAboutMe
	} = convertedProfile;

	const output: MentorSearchResult = {
		...convertedProfile,
		mentorProfileAboutMe: mentorProfileAboutMe ? mentorProfileAboutMe : "",
		terms: terms === undefined ? [] : terms,
		hasLinkedZoomAccount:
			hasLinkedZoomAccount === undefined ? false : hasLinkedZoomAccount,
		canAcceptPayments:
			canAcceptPayments === undefined ? false : canAcceptPayments,
		hasSkypeId: hasSkypeId === undefined ? false : hasSkypeId,
		sortindex
	};

	return output;
}
